import * as React from 'react'
import { FieldGroup } from '@/components/ui/field'
import { SlideOver } from '@/components/ui/slide-over'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { SaveBar, type SaveState } from '@/components/visitone/save-bar'
import { useRegisterUnsavedChanges, useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { useAllPeopleUsers, usePeopleMutations } from '@/hooks/use-people'
import type { RoleRow } from '@/lib/bff/people'
import { ChoiceItem } from '@/components/visitone/surfaces/people/people-shared'
import { changedCount, errorStatus, personName, roleLabel } from '@/lib/people/people-text'

type Problem = 'changedMeanwhile' | 'refused' | null

/*
 * Members of a role (VIS-332): who holds it, saved with the panel's SaveBar
 * (VIS-321). The caller's own row and anyone for whom this is the only role
 * are shown as text with the reason, never as disabled checkboxes.
 */
export function MembersPanel({
  role,
  onClose,
  onSaved,
}: {
  role: RoleRow | null
  onClose: () => void
  onSaved: (role: RoleRow) => void
}) {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  const { confirmThen } = useUnsavedChanges()
  const open = role !== null
  const users = useAllPeopleUsers(open)
  const mutations = usePeopleMutations()

  const label = role ? roleLabel(t, role) : ''
  const initial = React.useMemo(
    () => (role ? (users.people ?? []).filter((person) => person.roles.some((held) => held.id === role.id)).map((person) => person.userId) : []),
    [role, users.people],
  )
  const [selected, setSelected] = React.useState<string[] | null>(null)
  const [problem, setProblem] = React.useState<Problem>(null)
  const [saveState, setSaveState] = React.useState<SaveState>('idle')
  const [shownFor, setShownFor] = React.useState(role)
  if (role !== shownFor) {
    setShownFor(role)
    setSelected(null)
    setProblem(null)
    setSaveState('idle')
  }

  const current = selected ?? initial
  const count = changedCount(initial, current)
  const discard = React.useCallback(() => {
    setSelected(null)
    setProblem(null)
    setSaveState('idle')
  }, [])
  useRegisterUnsavedChanges(open ? { count, subject: t('people.members.subject', { role: label }), discard } : null)

  const save = async () => {
    if (!role || saveState === 'saving') return
    setProblem(null)
    setSaveState('saving')
    try {
      await mutations.setRoleMembers(role.id, current)
      setSaveState('idle')
      onSaved(role)
    } catch (error) {
      const status = errorStatus(error)
      if (status === 403) {
        setSaveState('idle')
        setProblem('refused')
      } else if (status === 404 || status === 409) {
        setSaveState('idle')
        setProblem('changedMeanwhile')
        void mutations.revalidate()
      } else {
        setSaveState('failed')
      }
    }
  }

  const toggle = (userId: string, next: boolean) => {
    setSelected((value) => {
      const base = value ?? initial
      return next ? [...base, userId] : base.filter((id) => id !== userId)
    })
    if (saveState === 'failed') setSaveState('idle')
  }

  return (
    <SlideOver
      open={open}
      title={t('people.members.title', { role: label })}
      onClose={onClose}
      footer={
        <SaveBar
          docked="panel"
          count={count}
          state={saveState}
          onDiscard={() => confirmThen({ kind: 'discard' }, discard)}
          onSave={() => void save()}
        />
      }
    >
      {role ? (
        <div className="flex flex-col gap-5">
          {problem === 'changedMeanwhile' ? (
            <OperationFeedback state="error" title={t('people.members.changedMeanwhile', { role: label })} />
          ) : null}
          {problem === 'refused' ? (
            <OperationFeedback
              state="error"
              title={t('core.access.actionRefusedTitle')}
              detail={t('core.access.actionRefusedBody', { tenant })}
            />
          ) : null}
          {users.error ? (
            <OperationFeedback
              state="error"
              title={t('people.users.loadFailed.title')}
              detail={t('people.users.loadFailed.body')}
              onRetry={() => void users.revalidate()}
            />
          ) : !users.people ? (
            <p role="status" className="text-sm text-fg-muted">
              {t('people.members.loading')}
            </p>
          ) : (
            <FieldGroup id="role-members" legend={t('people.members.legend', { role: label })} help={t('people.members.help')}>
              <ul className="mt-1 flex flex-col">
                {users.people.map((person) => {
                  const name = personName(person)
                  const held = initial.includes(person.userId)
                  const onlyThisRole = held && person.roles.length === 1
                  const locked = person.isSelf
                    ? t('people.row.cannotChangeSelf')
                    : onlyThisRole
                      ? t('people.members.onlyRole', { name })
                      : undefined
                  return (
                    <ChoiceItem
                      key={person.userId}
                      id={`member-${person.userId}`}
                      label={name}
                      description={person.name && person.email ? person.email : undefined}
                      locked={locked}
                      pill={person.isSelf ? t('people.row.you') : undefined}
                      checked={current.includes(person.userId)}
                      onChange={(next) => toggle(person.userId, next)}
                    />
                  )
                })}
              </ul>
            </FieldGroup>
          )}
        </div>
      ) : null}
    </SlideOver>
  )
}
